import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { UserService } from '../userService';
import { RoleService, Role } from '../roleService';
import { CreateUserDto } from '../dto/createUserDto';
import { ListUserDto } from '../dto/listUserDto';
import { RestResponse } from '../../../shared/RestResponse';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.html',
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    MessageModule,
    ProgressSpinnerModule,
    DividerModule
  ]
})
export class UserFormComponent implements OnInit {
  userForm!: FormGroup;
  isLoading: boolean = false;
  errorMessage: string = '';
  isEditMode: boolean = false;
  userId: number | null = null;
  roles: Role[] = [];
  isDriver: boolean = false;

  constructor(
    private userService: UserService,
    private roleService: RoleService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadRoles();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.userId = +params['id'];
        this.isEditMode = true;
        this.loadUser();
      }
    });
  }

  private loadRoles(): void {
    this.roleService.list().subscribe({
      next: (response: RestResponse<Role[]>) => {
        this.roles = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar roles:', error);
      }
    });
  }

  private initializeForm(): void {
    this.userForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      roleId: [null, [Validators.required]],
      cnh: ['']
    });

    this.userForm.get('roleId')?.valueChanges.subscribe(roleId => {
      this.isDriver = roleId === 4;
      const cnhControl = this.userForm.get('cnh');
      if (this.isDriver) {
        cnhControl?.setValidators([Validators.required]);
      } else {
        cnhControl?.clearValidators();
        cnhControl?.setValue('');
      }
      cnhControl?.updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  private loadUser(): void {
    if (this.userId) {
      this.isLoading = true;
      this.userService.getById(this.userId).subscribe({
        next: (response: RestResponse<ListUserDto>) => {
          const responseData = response.data as any;
          const userData = responseData?.model || responseData;
          
          if (!userData) {
            this.errorMessage = 'Dados do usuário não encontrados.';
            this.isLoading = false;
            return;
          }
          
          const roleId = userData.roleId ? Number(userData.roleId) : null;
          this.isDriver = roleId === 4;
          
          const cnhControl = this.userForm.get('cnh');
          if (this.isDriver) {
            cnhControl?.setValidators([Validators.required]);
          } else {
            cnhControl?.clearValidators();
          }
          cnhControl?.updateValueAndValidity();
          
          this.userForm.patchValue({
            name: userData.name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            roleId: roleId,
            cnh: userData.cnh || ''
          }, { emitEvent: false });
          
          this.cdr.detectChanges();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao carregar dados do usuário.';
          console.error('Erro ao carregar usuário:', error);
        }
      });
    }
  }

  onSubmit(): void {
    if (this.userForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const userData: CreateUserDto = this.userForm.value;

      if (this.isEditMode && this.userId) {
        this.updateUser(userData);
      } else {
        this.createUser(userData);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private createUser(userData: CreateUserDto): void {
    this.userService.create(userData).subscribe({
      next: (response: RestResponse<void>) => {
        this.isLoading = false;
        this.router.navigate(['/users']);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Erro ao cadastrar usuário. Tente novamente.';
        console.error('Erro ao criar usuário:', error);
      }
    });
  }

  private updateUser(userData: CreateUserDto): void {
    if (this.userId) {
      this.userService.update(this.userId, userData).subscribe({
        next: (response: RestResponse<CreateUserDto>) => {
          this.isLoading = false;
          this.router.navigate(['/users']);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao atualizar usuário. Tente novamente.';
          console.error('Erro ao atualizar usuário:', error);
        }
      });
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter pelo menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['email']) {
        return `${this.getFieldLabel(fieldName)} deve ser um email válido`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'name': 'Nome',
      'email': 'Email',
      'phone': 'Telefone',
      'roleId': 'Função',
      'cnh': 'CNH'
    };
    return labels[fieldName] || fieldName;
  }

  onCancel(): void {
    this.router.navigate(['/users']);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Usuário' : 'Cadastrar Usuário';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return this.isEditMode ? 'Atualizando...' : 'Cadastrando...';
    }
    return this.isEditMode ? 'Atualizar' : 'Cadastrar';
  }
}
